/**
 * Classement Live de l'espace compétition.
 *
 * Affiche le classement en temps réel des joueurs avec points,
 * pourcentages attaque/défense et statistiques top 5.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { PlayerAvatar } from '../../components/PlayerAvatar';
import ClickablePseudo from '../../components/ClickablePseudo';
import type { Session } from '../../types/app';
import { BACKEND_URL } from '../../config';

const PAGE_SIZE = 50;

interface LiveItem {
  pseudo: string;
  pseudo_display?: string;
  pfinal: number;
  ptot: number;
  patt: number;
  pdef: number;
  nb?: number;
  victoire?: number;
  top5?: number;
  team_name?: string;
  /** Rang réel dans le live (API) ; absent sur anciennes réponses */
  rank?: number;
}

interface LiveRankingProps {
  session: Session;
}

function fmtPct(v: number | null | undefined): string {
  if (v === null || v === undefined) return '-';
  const int = Math.floor(v);
  const dec = String(Math.round((v - int) * 100)).padStart(2, '0');
  return `${int},${dec}%`;
}

export default function LiveRanking({ session: _session }: LiveRankingProps) {
  const [items, setItems] = useState<LiveItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [error, setError] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const loadingMore = useRef(false);

  const fetchData = useCallback(async (offset: number, searchTerm: string, append: boolean) => {
    try {
      if (!append) setLoading(true);
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset),
      });
      if (searchTerm) params.set('search', searchTerm);

      const res = await fetch(`${BACKEND_URL}/api/rankings/competition/live?${params}`);
      const json = await res.json();
      if (!json.success) throw new Error(json.error || 'fetch_failed');

      const data = json.data;
      const newItems: LiveItem[] = data.items ?? [];

      if (append) {
        setItems(prev => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }
      setTotal(data.total ?? data.count ?? newItems.length);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur de chargement');
    } finally {
      setLoading(false);
      loadingMore.current = false;
    }
  }, []);

  useEffect(() => {
    fetchData(0, search, false);
  }, [fetchData, search]);

  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore.current) return;
    if (items.length >= total) return;

    const { scrollTop, scrollHeight, clientHeight } = el;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      loadingMore.current = true;
      fetchData(items.length, search, true);
    }
  }, [items.length, total, search, fetchData]);

  if (error && items.length === 0) {
    return <div className="compet-error">{error}</div>;
  }

  return (
    <div className="live-ranking">
      <div className="live-ranking-toolbar">
        <input
          className="live-ranking-search"
          type="text"
          placeholder="Rechercher un joueur..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <span className="live-ranking-count">
          {total > 0 ? `${total} joueur${total > 1 ? 's' : ''}` : ''}
        </span>
      </div>

      {loading && items.length === 0 ? (
        <div className="compet-loading">Chargement...</div>
      ) : items.length === 0 ? (
        <div className="compet-empty">Aucun résultat</div>
      ) : (
        <div className="live-ranking-list" ref={listRef} onScroll={handleScroll}>
          <table className="live-ranking-table">
            <thead>
              <tr>
                <th className="rank-col">#</th>
                <th>Joueur</th>
                <th className="num-col">Points</th>
                <th className="num-col">%age</th>
                <th className="num-col">Att</th>
                <th className="num-col">Déf</th>
                <th className="num-col">Top 5</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={item.pseudo}>
                  <td className="rank-col">{item.rank ?? idx + 1}</td>
                  <td>
                    <div className="live-ranking-table pseudo-col">
                      <PlayerAvatar pseudo={item.pseudo} size={28} />
                      <ClickablePseudo pseudo={item.pseudo}>
                        {item.pseudo_display || item.pseudo}
                      </ClickablePseudo>
                      {item.team_name && (
                        <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>
                          [{item.team_name}]
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="num-col">{fmtPct(item.pfinal)}</td>
                  <td className="num-col">{fmtPct(item.ptot)}</td>
                  <td className="num-col">{fmtPct(item.patt)}</td>
                  <td className="num-col">{fmtPct(item.pdef)}</td>
                  <td className="num-col">
                    {item.top5 != null && item.nb != null
                      ? `${item.top5} / ${item.nb}`
                      : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
